import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchScenarioViewMetadata } from 'hooks/ppa/useFetchViewMetadata';
import type { PPAViewDataSource } from 'types/ppaViews';
import { getAuthUserMail } from 'utils/helperMethods';

const LENS_SETTINGS_SEARCH_DEBOUNCE_MS = 400;

export const getPpaLensSettingsSearchQueryKey = (
    viewId: number,
    scenarioId: number | null,
    dataSource: PPAViewDataSource,
    searchKeyword: string,
    userId?: string | null,
) =>
    [
        'ppa-lens-settings-search',
        viewId,
        scenarioId,
        dataSource,
        searchKeyword.trim(),
        (userId ?? getAuthUserMail()) || null,
    ] as const;

type UseLensSettingsSearchArgs = {
    viewId: number | null;
    scenarioId: number | null;
    dataSource?: PPAViewDataSource;
    searchInput: string;
    enabled: boolean;
};

export function useLensSettingsSearch({
    viewId,
    scenarioId,
    dataSource = 'view',
    searchInput,
    enabled,
}: UseLensSettingsSearchArgs) {
    const [debouncedKeyword, setDebouncedKeyword] = useState('');

    const debouncedSetKeyword = useMemo(
        () =>
            debounce((value: string) => {
                setDebouncedKeyword(value.trim());
            }, LENS_SETTINGS_SEARCH_DEBOUNCE_MS),
        [],
    );

    useEffect(() => () => debouncedSetKeyword.cancel(), [debouncedSetKeyword]);

    useEffect(() => {
        const trimmed = searchInput.trim();
        if (!trimmed) {
            debouncedSetKeyword.cancel();
            setDebouncedKeyword('');
            return;
        }
        debouncedSetKeyword(trimmed);
    }, [debouncedSetKeyword, searchInput]);

    const submitSearch = useCallback(
        (override?: string) => {
            const nextValue = (override !== undefined ? override : searchInput).trim();
            debouncedSetKeyword.cancel();
            setDebouncedKeyword(nextValue);
        },
        [debouncedSetKeyword, searchInput],
    );

    const isSearchActive = debouncedKeyword.length > 0;

    const query = useQuery({
        queryKey:
            viewId != null && isSearchActive
                ? getPpaLensSettingsSearchQueryKey(viewId, scenarioId, dataSource, debouncedKeyword)
                : ['ppa-lens-settings-search', 'idle'],
        queryFn: () =>
            fetchScenarioViewMetadata(viewId as number, {
                scenarioId,
                dataSource,
                searchKeyword: debouncedKeyword,
            }),
        enabled: enabled && viewId != null && isSearchActive,
        staleTime: 1000 * 30,
    });

    return {
        isSearchActive,
        debouncedKeyword,
        submitSearch,
        searchResult: query.data ?? null,
        isSearching: query.isFetching,
    };
}
